import React, { useEffect, useState } from "react";
import { Skeleton } from "../../components/ui/Skeleton";
import { portalApi } from "../../api/portalApi";
import { PortalDisputeDetail } from "../../types";

/** Semantic dispute status pills per portal design spec */
function disputeStatusPillClass(statusLabel: string): string {
  const s = statusLabel.toLowerCase();
  if (s.includes("overdue")) {
    return "border border-[#FECACA] bg-[#FEF2F2] text-[#991B1B]";
  }
  if (s.includes("removed") || s.includes("resolved") || s.includes("great news")) {
    return "border border-emerald-200 bg-emerald-50 text-emerald-800";
  }
  if (s.includes("await") || s.includes("waiting") || s.includes("response")) {
    return "border border-[#FCD34D] bg-[#FEF3C7] text-[#78350F]";
  }
  if (s.includes("letter") || s.includes("sent")) {
    return "border border-[#BFDBFE] bg-[#EFF6FF] text-navy-600";
  }
  if (s.includes("disput") || s.includes("working") || s.includes("investigat")) {
    return "border border-emerald-200 bg-emerald-100 text-emerald-800";
  }
  return "border border-slate-200 bg-slate-100 text-slate-800";
}

const PortalDisputesPage: React.FC = () => {
  const [rows, setRows] = useState<PortalDisputeDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      setError("");
      try {
        const data = await portalApi.disputes();
        setRows(data);
      } catch {
        setError("We couldn’t load your disputes. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    void run();
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-navy-900">My disputes</h1>
        <p className="mt-1 text-sm text-slate-600">
          Plain-language updates on items we’re working on for you — no jargon.
        </p>
      </div>

      {error && (
        <div className="rounded-lg border border-[#FCD34D] bg-[#FEF3C7] px-4 py-3 text-sm text-[#78350F]">{error}</div>
      )}

      {loading && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" aria-busy="true" aria-label="Loading disputes">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-36 rounded-lg bg-slate-200/90" />
          ))}
        </div>
      )}

      {!loading && !error && rows.length === 0 && (
        <div className="rounded-lg border border-slate-200 bg-white p-10 text-center text-slate-600 shadow-sm">
          You don’t have any disputes on file yet. Your advisor will add accounts as your program moves forward.
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {!loading &&
          rows.map((d) => (
            <article
              key={d.id}
              className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm transition-colors hover:bg-slate-50"
            >
              <p className="text-xs font-medium uppercase tracking-[0.05em] text-slate-600">{d.creditor}</p>
              <h2
                className={`mt-2 inline-block max-w-full rounded-full px-3 py-1 text-sm font-medium ${disputeStatusPillClass(d.statusLabel)}`}
              >
                {d.statusLabel}
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-slate-800">{d.friendlySummary}</p>
              <p className="mt-4 text-xs text-slate-400">
                Last update {new Date(d.updatedAt).toLocaleDateString(undefined, { dateStyle: "medium" })}
              </p>
            </article>
          ))}
      </div>
    </div>
  );
};

export default PortalDisputesPage;
